/**
 * The providers Settings › AI offers. Each one's code (anthropic.ts, openai.ts) loads only when it's used. The origin
 * of its API must be in the Content-Security-Policy's connect-src (shared/csp.ts), which a test checks.
 */
import { DEFAULT_AI_MODEL, isAiProviderId as isServerAiProviderId } from '@shared/ai';
import type { AiClient, AiProviderId } from './types';
import { withUsage } from './usage';

/** Set by vite.config.ts: true in dev builds and tests, false in production; absent where Vite isn't (npm run eval). */
declare const __AI_SIMULATOR__: boolean | undefined;
/** Whether this build has the AI simulator (simulator.ts). */
export const AI_SIMULATOR = typeof __AI_SIMULATOR__ !== 'undefined' && __AI_SIMULATOR__;

/** A provider this build can ask: the simulator only where it's built in. */
export const isAiProviderId = (value: unknown): value is AiProviderId => isServerAiProviderId(value) || (AI_SIMULATOR && value === 'simulator');

/** A model Settings › AI offers (models.ts adds the provider's newer ones to these). */
export interface AiModel {
  id: string;
  label: string;
  /** Under the dropdown while it's picked: what it's good for, and roughly what a suggestion costs. */
  hint: string;
}

export interface AiProvider {
  id: AiProviderId;
  name: string;
  /** The models Taretto was evaluated with, in the dropdown's order, the default first. */
  models: readonly AiModel[];
  /** What a new setup starts with. */
  defaultModel: string;
  /** For small questions a cheap model answers as well (tastingNotes.ts), whatever model the key is set up with. */
  smallModel: string;
  keyPlaceholder: string;
  /** Needs no key (the simulator): Settings › AI saves this one and shows no key field. */
  keyless?: string;
  /** Where to create a key, and give it a spend limit. */
  keysUrl: string;
  load(): Promise<AiClient>;
}

export const AI_PROVIDERS: Record<AiProviderId, AiProvider> = {
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    // In an evaluation of 19 dial-in cases, Sonnet 5.5 got 18 right, Opus 5.5 all 19 at twice the cost, and Haiku 5.5
    // about 72% at a sixteenth of the cost: it nearly always changes the grind.
    models: [
      { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', hint: 'Recommended: fast, and nearly always right. About 1 cent a suggestion.' },
      { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', hint: 'The cheapest, well under 1 cent a suggestion, but its suggestions are less reliable.' },
      { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', hint: 'The most careful, about 2 cents a suggestion.' },
    ],
    defaultModel: DEFAULT_AI_MODEL.anthropic,
    smallModel: 'claude-haiku-5-5',
    keyPlaceholder: 'sk-ant-…',
    keysUrl: 'https://platform.claude.com/settings/keys',
    load: async () => withUsage((await import('./anthropic')).anthropicClient),
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    models: [
      { id: 'gpt-6-luna', label: 'GPT-6 Luna', hint: 'Small, fast and cheap, well under 1 cent a suggestion.' },
      { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', hint: 'A larger model, about 1 cent a suggestion.' },
    ],
    defaultModel: DEFAULT_AI_MODEL.openai,
    smallModel: 'gpt-6-luna',
    keyPlaceholder: 'sk-…',
    keysUrl: 'https://platform.openai.com/api-keys',
    load: async () => withUsage((await import('./openai')).openaiClient),
  },
  // Dev builds only, like the scale simulator: __AI_SIMULATOR__ is false in a production build, which then drops it.
  simulator: {
    id: 'simulator',
    name: 'Simulator',
    models: [{ id: 'simulated', label: 'Simulated model', hint: 'Answers from this dev build itself: nothing is sent anywhere or billed.' }],
    defaultModel: 'simulated',
    smallModel: 'simulated',
    keyPlaceholder: '',
    keyless: 'simulated',
    keysUrl: '',
    load: AI_SIMULATOR
      ? async () => (await import('./simulator')).simulatorClient
      : async () => {
          throw new Error('The AI simulator is only in dev builds');
        },
  },
};

/** The providers Settings › AI offers, in its order: the simulator last, in a dev build only. */
export const AI_PROVIDER_IDS = (Object.keys(AI_PROVIDERS) as AiProviderId[]).filter((id) => AI_SIMULATOR || id !== 'simulator');
